import * as Ariakit from '@ariakit/react';
import { NavLink } from 'react-router-dom';
import { css, styled } from 'styled-components';
import { MouseEventHandler, useContext } from 'react';

import { IconNames } from '../../../assets';
import { theme } from '../../../styles';
import { Flex } from '../../Layout';
import { Assets } from './NavbarPopoverMenu';
import { getUrlType } from '../../../utils';
import { staticTextSmallStyle } from '../common/commonStyles';
import { NavbarHamburgerMenuContext, NavbarMenuSubMenuContext } from './contexts';

type NavbarMenuItemCommonProps = {
  name: string;
  iconName?: IconNames;
  /** description not shown in hamburger menu version */
  description?: string;
};
export const NavbarMenuItemContent = ({ iconName, name, description }: NavbarMenuItemCommonProps) => {
  const Icon = iconName ? Assets[iconName] : undefined;
  const { inNavbarHamburgerMenu } = useContext(NavbarHamburgerMenuContext);
  const { inNavbarMenuSubMenu } = useContext(NavbarMenuSubMenuContext);

  return (
    <Flex columnGap={8} alignItems={description ? 'flex-start' : 'center'}>
      {Icon && <Icon width={22} color={inNavbarHamburgerMenu ? 'foraBlue' : 'black'} />}
      <Flex flexDirection="column" rowGap={5}>
        {inNavbarHamburgerMenu ? (
          <ItemNameHamburger $size={inNavbarMenuSubMenu ? 'standard' : 'medium'}>{name}</ItemNameHamburger>
        ) : (
          <ItemNamePopover>{name}</ItemNamePopover>
        )}
        {description && !inNavbarHamburgerMenu && <ItemDescription>{description}</ItemDescription>}
      </Flex>
    </Flex>
  );
};

type NavbarMenuItemLinkProps = NavbarMenuItemCommonProps & {
  /** Start with "/" for internal links - https for external links  */
  to: string;
};
export const NavbarMenuItemLink = ({ to, ...props }: NavbarMenuItemLinkProps) => {
  const urlType = getUrlType(to);
  const { inNavbarMenuSubMenu } = useContext(NavbarMenuSubMenuContext);
  const { inNavbarHamburgerMenu } = useContext(NavbarHamburgerMenuContext);

  return (
    <AriaMenuItem
      aria-label={props.name}
      $isSubMenuItem={inNavbarMenuSubMenu}
      $isHamburgermenuItem={inNavbarHamburgerMenu}
      key={props.name}
      render={
        urlType === 'external' ? (
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a href={to} target="_blank" rel="noopener noreferrer" />
        ) : (
          <NavLink to={to} />
        )
      }
    >
      <NavbarMenuItemContent {...props} />
    </AriaMenuItem>
  );
};

type NavbarMenuItemButtonProps = NavbarMenuItemCommonProps & { onClick: MouseEventHandler<HTMLButtonElement> };

export const NavbarMenuItemButton = ({ onClick, ...props }: NavbarMenuItemButtonProps) => {
  const { inNavbarMenuSubMenu } = useContext(NavbarMenuSubMenuContext);
  const { inNavbarHamburgerMenu } = useContext(NavbarHamburgerMenuContext);

  return (
    <AriaMenuItem
      $isButton
      $isSubMenuItem={inNavbarMenuSubMenu}
      $isHamburgermenuItem={inNavbarHamburgerMenu}
      key={props.name}
      render={<button onClick={onClick} aria-label={props.name} />}
    >
      <NavbarMenuItemContent {...props} />
    </AriaMenuItem>
  );
};

export const AriaMenuItem = styled(Ariakit.MenuItem)<{
  $isButton?: boolean;
  $isSubMenuItem?: boolean;
  $isHamburgermenuItem?: boolean;
}>`
  // Reset styling if button
  ${({ $isButton }) =>
    $isButton &&
    css`
      all: unset;
    `}

  display: block;
  width: 100%;
  padding: ${({ $isSubMenuItem }) => ($isSubMenuItem ? '15px 20px 15px 80px' : '20px 20px')};

  box-sizing: border-box;
  color: ${theme.colors.black90};
  text-decoration: none;
  cursor: pointer;

  background-color: ${({ $isSubMenuItem }) => ($isSubMenuItem ? theme.colors.black05 : theme.colors.white)};
  // react-router adds a class called "active" if you're on the page
  // https://reactrouter.com/en/main/components/nav-link#default-active-class
  &.active {
    background-color: ${theme.colors.black10};
  }
  &:hover,
  &[data-active-item] {
    background-color: ${theme.colors.blueHover};
  }

  ${({ $isHamburgermenuItem }) =>
    $isHamburgermenuItem
      ? css`
          border-bottom: 1px solid ${theme.colors.black20};
        `
      : css`
          &:not(:last-child) {
            border-bottom: 1px solid ${theme.colors.black20};
          }
        `}
`;

const ItemNamePopover = styled.p`
  ${theme.typography.textStandard};
  font-weight: 600;
  margin: 0;

  ${AriaMenuItem}.active &,
  ${AriaMenuItem}:hover & {
    text-decoration: underline;
  }
`;

const ItemNameHamburger = styled.p<{ $size?: 'standard' | 'medium' }>`
  ${({ $size }) => ($size === 'standard' ? theme.typography.textStandard : theme.typography.textMedium)}
  margin: 0;

  ${AriaMenuItem}.active &,
  ${AriaMenuItem}:hover & {
    text-decoration: underline;
  }
`;

const ItemDescription = styled.p`
  ${staticTextSmallStyle};
`;
